"use client";

import { ComponentProps } from "react";

import Button from "~/componentsDS/Button";
import useQuizLink from "./hooks/useQuizLink";

type Props = ComponentProps<typeof Button> & {
    quizSearchParams?: Record<string, string>;
};

const appendQuizSearchParams = (
    href: string,
    quizSearchParams?: Record<string, string>
) => {
    if (!quizSearchParams || !href) return href;

    const [path, search = ""] = href.split("?");
    const nextSearch = new URLSearchParams(search);
    Object.entries(quizSearchParams).forEach(([key, value]) => {
        nextSearch.set(key, value);
    });
    const queryString = nextSearch.toString();
    return queryString ? `${path}?${queryString}` : path;
};

/**
 * Drop-in replacement for `<Button href={QUIZ_ROUTES.X}>` that resolves the quiz's
 * destination before navigating, so a user who does not need to take the quiz goes
 * straight there instead of flashing the quiz page first. Safe to render from a
 * server component — only this leaf is client-rendered.
 *
 * This is the one wrapper for `componentsDS/Button` CTAs; components that render
 * their own link element call `useQuizLink` directly rather than wrapping it again.
 */
export const QuizCTA = ({
    href = "",
    quizSearchParams,
    ...buttonProps
}: Props) => {
    const { href: resolvedHref } = useQuizLink(href);
    const finalHref = appendQuizSearchParams(resolvedHref, quizSearchParams);

    return <Button {...buttonProps} href={finalHref} />;
};
